/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 16/05/2025 16:40
 *
 */

import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  AuthorizationService,
  DataModel,
  InitService,
  Logger,
} from '@infodata-group/inf-commons';
import { AlarmGateway } from '../gateway/alarm/alarm.gateway';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlarmLite } from '../model/alarm/alarm-lite';
import { map } from 'rxjs/operators';
import { Alarm } from '../model/alarm/alarm';
import { AlarmUpdateDto } from '../dto/alarm/alarm-update.dto';
import { VigiHttpFilter } from '../modules/shared/engine/vigi-http-filter';
import { Tools } from '../modules/shared/engine/tools';

const REFRESH_INTERVAL = 1000 * 60 * 5; // ms * s * m

@Injectable({
  providedIn: 'root',
})
export class AlarmsService {
  private readonly gateway = inject(AlarmGateway);
  private readonly initService = inject(InitService);
  private readonly authorizationService = inject(AuthorizationService);

  private alarmsLite$$ = new BehaviorSubject<AlarmLite[]>([]);
  public alarmsLite$ = this.alarmsLite$$.asObservable();
  private lastDateRefresh$$ = new BehaviorSubject<Date>(new Date());
  public lastDateRefresh$ = this.lastDateRefresh$$.asObservable();
  public hasAlarms$ = this.alarmsLite$$.pipe(
    map((alarms) => alarms.length > 0),
  );

  public autoRefreshTimeout: any;
  public isLoading = false;

  constructor() {
    this.listenInitService();
    this.listenAuthService();
  }

  private listenInitService(): void {
    this.initService.isLoaded$.pipe(takeUntilDestroyed()).subscribe({
      next: (isLoaded) => {
        if (isLoaded) {
          this.refresh();
        }
      },
      error: (e) => {
        Logger.w(e);
      },
    });
  }
  private listenAuthService(): void {
    this.authorizationService.subscribe(() => {
      this.refresh();
    });
  }

  public loadAlarmsLite(): void {
    this.isLoading = true;
    this.gateway.getLite$().subscribe({
      next: (alarmsLite) => {
        this.alarmsLite$$.next(alarmsLite);
        this.lastDateRefresh$$.next(new Date());
        this.isLoading = false;
      },
      error: (e) => {
        Tools.errorObserver(e);
        this.isLoading = false;
      },
    });
  }

  public loadAlarms$(httpFilter?: VigiHttpFilter): Observable<Alarm[]> {
    return this.gateway.get$(httpFilter);
  }

  public loadDataModel$(): Observable<DataModel> {
    return this.gateway.options$();
  }

  public updateAlarms$(dto: AlarmUpdateDto): Observable<unknown> {
    return this.gateway.update$(dto);
  }

  public refresh(clearAutoRefresh = false): void {
    if (clearAutoRefresh) {
      this.clearTimeout();
    }
    if (!this.authorizationService.isAuthenticated) {
      return;
    }
    this.loadAlarmsLite();
    this.autoRefresh();
  }

  public clearTimeout(): void {
    if (this.autoRefreshTimeout) {
      clearTimeout(this.autoRefreshTimeout);
    }
  }

  public clearAlarm(): void {
    this.alarmsLite$$.next([]);
    this.lastDateRefresh$$.next(new Date());
  }

  private autoRefresh(): void {
    this.autoRefreshTimeout = setTimeout(() => {
      this.refresh();
    }, REFRESH_INTERVAL);
  }
}
